import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import LiveFeed from "@/components/LiveFeed";
import { DASHBOARD } from "@/constants/testIds";
import { t } from "@/lib/i18n";
import {
  Smartphone,
  Users,
  Video as VideoIcon,
  BellRing,
  Activity,
  Heart,
  Eye,
  Sparkles,
  Wifi,
  BatteryFull,
  RefreshCw,
} from "lucide-react";
import {
  AreaChart,
  Area,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";

const kpiList = [
  { key: "devices_online", icon: Smartphone, format: (s) => `${s.counts.devices_online}/${s.counts.devices_total}` },
  { key: "accounts_total", icon: Users, format: (s) => s.counts.accounts_total },
  { key: "videos_queued", icon: VideoIcon, format: (s) => s.counts.videos_queued },
  { key: "alerts_unack", icon: BellRing, format: (s) => s.counts.alerts_unack, tone: (s) => (s.counts.alerts_unack ? "rose" : "slate") },
];

const kpiSecondary = [
  { key: "total_followers", icon: Users, format: (s) => s.kpis.total_followers.toLocaleString("ru-RU") },
  { key: "views_estimated_7d", icon: Eye, format: (s) => s.kpis.views_estimated_7d.toLocaleString("ru-RU") },
  { key: "avg_health_score", icon: Activity, format: (s) => `${s.kpis.avg_health_score}%` },
];

export default function Dashboard() {
  const [summary, setSummary] = useState(null);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);

  const load = async () => {
    try {
      const [s, d] = await Promise.all([api.summary(), api.devices()]);
      setSummary(s);
      setDevices(d);
    } catch { toast.error("Не удалось загрузить дашборд"); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const tm = setInterval(load, 15000);
    return () => clearInterval(tm);
  }, []);

  const seedDemo = async () => {
    setSeeding(true);
    try {
      const res = await api.seed(false);
      toast.success(res.note ? "Демо-данные уже есть" : "Демо-данные загружены");
      await load();
    } catch { toast.error("Не удалось загрузить демо-данные"); }
    finally { setSeeding(false); }
  };

  return (
    <div data-testid={DASHBOARD.root}>
      <PageHeader
        title={t.dashboard.title}
        description={t.dashboard.description}
        actions={
          <button
            data-testid={DASHBOARD.seedBtn}
            onClick={seedDemo}
            disabled={seeding}
            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50 text-slate-700 text-sm font-medium transition-colors disabled:opacity-50"
          >
            <Sparkles className="h-4 w-4 text-emerald-500" />
            {seeding ? t.dashboard.seeding : t.dashboard.seed}
          </button>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {kpiList.map((k) => {
          if (!summary) return <SkeletonCard key={k.key} />;
          const tone = k.tone ? k.tone(summary) : "emerald";
          return (
            <div
              data-testid={DASHBOARD.kpiCard(k.key)}
              key={k.key}
              className="card-soft p-5"
            >
              <div className="flex items-center justify-between">
                <div className="text-xs font-medium uppercase tracking-widest text-slate-500">
                  {t.dashboard.kpi[k.key]}
                </div>
                <div
                  className={`h-8 w-8 rounded-lg flex items-center justify-center ${
                    tone === "rose"
                      ? "bg-rose-50 text-rose-500"
                      : "bg-emerald-50 text-emerald-500"
                  }`}
                >
                  <k.icon className="h-4 w-4" />
                </div>
              </div>
              <div className="font-display text-3xl font-semibold text-slate-900 mt-2">
                {k.format(summary)}
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
        <div className="lg:col-span-2 space-y-4">
          <div className="card-soft p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="font-display text-lg font-semibold text-slate-900">
                  {t.dashboard.momentum}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {t.dashboard.momentumSub}
                </div>
              </div>
              <button
                onClick={load}
                className="text-slate-400 hover:text-emerald-500 transition-colors"
                aria-label="refresh"
              >
                <RefreshCw className="h-4 w-4" />
              </button>
            </div>
            <div className="h-64">
              {summary && (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={summary.series_14d}>
                    <defs>
                      <linearGradient id="gViews" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#10b981" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="gLikes" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.25} />
                        <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 11, fill: "#94a3b8" }}
                      tickFormatter={(v) => v.slice(5)}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                    <Tooltip
                      contentStyle={{
                        background: "#fff",
                        border: "1px solid #e2e8f0",
                        borderRadius: 12,
                        fontSize: 12,
                      }}
                    />
                    <Area type="monotone" name="просмотры" dataKey="views" stroke="#10b981" strokeWidth={2} fill="url(#gViews)" />
                    <Area type="monotone" name="лайки" dataKey="likes" stroke="#3b82f6" strokeWidth={2} fill="url(#gLikes)" />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            {kpiSecondary.map((k) =>
              summary ? (
                <div key={k.key} className="card-soft p-5" data-testid={DASHBOARD.kpiCard(k.key)}>
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
                      <k.icon className="h-4 w-4" />
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-500">
                        {t.dashboard.kpi[k.key]}
                      </div>
                      <div className="font-display text-xl font-semibold text-slate-900">
                        {k.format(summary)}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <SkeletonCard key={k.key} />
              )
            )}
          </div>
        </div>

        <div>
          <LiveFeed height={520} />
        </div>
      </div>

      <div className="flex items-end justify-between mb-4">
        <div>
          <h2 className="font-display text-xl font-semibold text-slate-900">{t.dashboard.phones}</h2>
          <p className="text-sm text-slate-500">{t.dashboard.phonesSub}</p>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} tall />)}
        </div>
      ) : devices.length === 0 ? (
        <EmptyState title={t.dashboard.noDevices} description={t.dashboard.noDevicesHint} />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {devices.map((d) => (
            <DeviceTile key={d.id} device={d} />
          ))}
        </div>
      )}
    </div>
  );
}

function DeviceTile({ device }) {
  return (
    <div
      data-testid={DASHBOARD.deviceTile(device.id)}
      className="card-soft p-5 hover:-translate-y-0.5 transition-transform duration-200"
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="font-display text-lg font-semibold text-slate-900">{device.name}</div>
          <div className="text-xs text-slate-500 mt-0.5">
            {device.model} · {device.color}
          </div>
        </div>
        <StatusBadge status={device.status} />
      </div>

      <div className="flex items-center justify-center h-32 bg-gradient-to-b from-slate-50 to-white rounded-xl mb-4 overflow-hidden">
        <img
          src={device.image_url || "https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?crop=entropy&cs=srgb&fm=jpg&w=400&q=80"}
          alt=""
          className="h-full object-contain"
        />
      </div>

      <div className="grid grid-cols-2 gap-2 text-xs">
        <Stat icon={BatteryFull} label={`${device.battery ?? 0}%`} />
        <Stat icon={Wifi} label={device.status === "offline" ? "офлайн" : "онлайн"} />
      </div>
      {device.udid && (
        <div className="text-[10px] font-mono text-slate-400 mt-3 truncate" title={device.udid}>
          {device.udid}
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-1.5 text-slate-600">
      <Icon className="h-3.5 w-3.5 text-slate-400" />
      <span>{label}</span>
    </div>
  );
}

function SkeletonCard({ tall }) {
  return (
    <div className={`card-soft p-5 ${tall ? "h-64" : "h-24"} animate-pulse bg-slate-100/40`} />
  );
}

function EmptyState({ title, description }) {
  return (
    <div className="card-soft p-10 text-center">
      <div className="h-12 w-12 mx-auto rounded-2xl bg-emerald-50 text-emerald-500 flex items-center justify-center mb-3">
        <Heart className="h-5 w-5" />
      </div>
      <div className="font-display text-lg font-semibold text-slate-900">{title}</div>
      <div className="text-sm text-slate-500 mt-1">{description}</div>
    </div>
  );
}
